import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import { useMyMemberships } from '../gym/useMyMemberships'
import { useMyProfile } from '../profile/useMyProfile'

/**
 * A private profile cannot enter, because the standings are on a public page.
 * One tap makes it public right here, and the page reloads ready to join.
 */
export function PrivateProfileCallout({ onMadePublic }: { onMadePublic: () => Promise<void> }) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const { refresh } = useMyProfile()
  // A child's profile is the parent's, so there is nothing to tap.
  const isChildAccount = useMyMemberships().childAccount !== null
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  async function makePublic() {
    setSaving(true)
    setFailed(false)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: true }),
      })
      if (!res.ok) throw new Error()
      void refresh()
      await onMadePublic()
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      role="alert"
      className="flex flex-col gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5 shadow-sm sm:flex-row sm:items-center"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
          aria-hidden="true"
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-base font-bold">{t('competitions.privateProfileTitle')}</p>
        <p className="text-sm text-foreground/70">
          {isChildAccount
            ? t('competitions.privateProfileChildBody')
            : t('competitions.privateProfileBody')}
        </p>
        {failed && <p className="text-sm text-danger">{t('competitions.privateProfileFailed')}</p>}
      </div>

      {!isChildAccount && (
        <Button
          color="primary"
          size="lg"
          className="shrink-0 font-semibold"
          isLoading={saving}
          onPress={() => void makePublic()}
        >
          {t('competitions.privateProfileCta')}
        </Button>
      )}
    </div>
  )
}
